/* eslint-disable no-restricted-syntax */
import dayjs from 'dayjs';
import store from '@/store';
import { showToast } from 'vant';
import constants, { encryptedMarketplaceId } from '@/constants';
import { i18n } from '@/local/index';
import { Bridge } from '@/utils/bridge';
import { isAppEnv } from '@/utils/device';
import aHttp from '@/models/axiosConfig/aHttp';
import { persistShippingAddress } from '@/models/api/addressApi';
import { getAsinDetail, getAsinBatch } from '@/models/api/detailPageApi';
import { useClipboard } from '@vueuse/core';
import { showDialog } from 'vant';
import { v4 as uuidv4 } from 'uuid';
import { MEDIA_CENTRAL_LINKS } from '@/configs/common';

const { t } = i18n.global;
const ONE_DAY_TIMESTAMP = 24 * 60 * 60 * 1000;
const TIME_UNIT_ARR = ['D', 'h', 'm', 's']; // format array
const TIMESTAMP_FORMAT_DENOMINATOR_ARR = [
  24 * 60 * 60 * 1000,
  60 * 60 * 1000,
  60 * 1000,
  1000
];
const APP_BROWSE_MODE = {
  BROWSE_MODE: 'BROWSE_MODE',
  NORMAL_MODE: 'NORMAL_MODE'
};
const {
  MERCHANT_BADGE_MAP,
  AVAILABILITY_STATUS_ENUM,
  ASSOCIATES_HANDLER_HORIZONTE_URL,
  GSAPP_RW_SESSION_KEY,
  INTERACTION_TYPE,
  TABBAR_PAGE_APP_CODE,
  ADD_CART_AND_FAVORITES_STATUS,
  SHARE_MODULE: { WEBAPP_PROD_SUBDOMAIN }
} = constants;

export const getPurchaseSessionId = async () => {
  let sid = '';
  const { value } = await Bridge.callFnVal('getDataFromMemoryCache', {
    key: GSAPP_RW_SESSION_KEY
  });
  if (value && typeof value === 'string') {
    sid = value;
  } else {
    const { data } = await aHttp.get(ASSOCIATES_HANDLER_HORIZONTE_URL, {
      action: 'getSid'
    });
    await Bridge.callFnVal('saveDataToMemoryCache', {
      key: GSAPP_RW_SESSION_KEY,
      value: data.body
    });
    sid = data.body;
  }
  return sid;
};

// Obtain specifications copy based on asin
export const getTwisterByAsin = data => {
  const asin = data.asin || '';
  let asinValue = [];
  let asinText = '';
  const variations = data.twisterVariations?.variations || [];
  const dimensionValues = data.twisterVariations?.dimensionValues || [];
  variations.forEach(item => {
    if (item.asin === asin) {
      asinValue = item.values;
    }
  });
  asinValue.forEach((item, index) => {
    asinText += dimensionValues[index][item];
  });
  return {
    asinText,
    values: asinValue
  };
};

// Obtain asin values based on asin
export const passAsinValuesGetAsin = (values, variations) => {
  let asin = '';
  variations?.forEach(item => {
    if (item.values.toString() === values.toString()) {
      asin = item.asin;
    }
  });
  return asin;
};

export const getAddressList = async () => {
  const { result: isAuth } = await Bridge.callFnVal('isAuthenticated');
  if (!isAuth) {
    return {
      addressInfo: null,
      addressList: []
    };
  }
  const { value: encryptedAddress } = await Bridge.callFnVal(
    'getDataFromDiskCache',
    {
      key: 'addressList',
      saveByUserId: true
    }
  );
  if (JSON.stringify(encryptedAddress) === '{}' || !encryptedAddress) {
    return {
      addressInfo: null,
      addressList: []
    };
  }
  const { data } = await persistShippingAddress({
    encryptedAddress
  });
  const addressList = data ?? [];
  const { value: selectedAddressId } = await Bridge.callFnVal(
    'getDataFromDiskCache',
    {
      key: 'selectedAddressInfoId',
      saveByUserId: true
    }
  );
  let item = null;
  if (selectedAddressId) {
    item = addressList?.find(i => i.id === selectedAddressId);
  }
  if (!item) {
    item = addressList?.find(i => i.isDefault);
  }
  let addressInfo = {};
  if (!item) {
    addressInfo = addressList?.length > 0 ? addressList[0] : null;
  } else {
    addressInfo = item;
  }
  return {
    addressInfo,
    addressList,
    selectedAddressId: addressInfo?.id
  };
};
// Set shopping cart data to change status
export const setCartDataChangeStatus = value => {
  Bridge.callFn('saveDataToMemoryCache', {
    key: 'isCartDataChanged',
    value
  });
};
// Clear invalid data from shopping cart
export const clearInvalidDataFromCart = callBack => {
  Bridge.callFn('getShoppingCartList', {}, async res => {
    const list = res.list || [];
    const { data } = await getAsinBatch({
      ids: list.map(item => item.productItem.asin).toString(),
      marketplace: encryptedMarketplaceId,
      withCache: false,
      allowOtherOffer: false,
      returnableFlagSkipped: false
    })['catch'](() => ({ data: [] }));
    const InvalidData = data.filter(item => {
      return (
        item.availability?.status === AVAILABILITY_STATUS_ENUM.OUT_OF_STOCK ||
        !item.selectionEligible
      );
    });
    if (!InvalidData.length) {
      callBack({
        status: false
      });
    } else {
      Bridge.callFn(
        'deleteShoppingCartList',
        {
          asins: InvalidData.map(item => item.asin)
        },
        () => {
          callBack({
            status: true,
            count: InvalidData.length
          });
          setCartDataChangeStatus(true);
        }
      );
    }
  });
};

// Obtain shopping cart data, change status, and reset
export const getAndResetCartDataChangeStatus = callBack => {
  const k = 'key';
  Bridge.callFn(
    'getDataFromMemoryCache',
    {
      [k]: 'isCartDataChanged'
    },
    res => {
      if (res.value === true) {
        callBack(true);
      } else {
        callBack(false);
      }
    }
  );
};
// Call the detail interface, handle the data structure shopping cart orders, favorites are needed
const tidyDataStructure = async (
  asin,
  merchantId,
  count = 1,
  selected = false
) => {
  const { data } = await getAsinDetail({
    id: asin,
    // Wait for Android or ios to provide an interactive method before optimizing
    marketplace: encryptedMarketplaceId,
    allowOtherOffer: true,
    merchantId
  })['catch'](() => {
    return {
      data: {}
    };
  });
  const cartData = {
    count,
    productItem: {
      ...data,
      ourPrice: data.ourPrice?.amount,
      stocks: data.stockOnHand,
      gsBadge: MERCHANT_BADGE_MAP[data.merchantId],
      twisterDisplayString: getTwisterByAsin(data).asinText
    },
    selected, // Selected by default
    addTime: new Date().getTime(),
    pricesInStr: `${data.ourPrice?.amount}`
  };
  return cartData;
};
// add shopping cart
export const addCartList = async (
  asin,
  merchantId,
  count = 1,
  selected = false,
  callBack,
  isFromCart
) => {
  const cartData = await tidyDataStructure(asin, merchantId, count, selected);
  Bridge.callFn('addShoppingCartList', { item: cartData }, res => {
    // Product out of stock or The product has been removed from the shelves
    if (
      cartData.productItem.availability?.status ===
        AVAILABILITY_STATUS_ENUM.OUT_OF_STOCK ||
      !cartData.productItem.selectionEligible
    ) {
      showToast({
        message: t('common.PRODUCTS_HAVE_BEEN_SOLD_OUT_UNABLE_ADD_CART'),
        className: 'a-toast'
      });
      return;
    }
    // Single product exceeds maximum limit
    if (
      res.result ===
      ADD_CART_AND_FAVORITES_STATUS.SINGLE_PRODUCT_EXCEED_MAX_LIMIT
    ) {
      showToast({
        message: t('common.SINGLE_PRODUCT_EXCEED_MAX_LIMIT'),
        className: 'a-toast'
      });
      return;
    }
    if (res.result === ADD_CART_AND_FAVORITES_STATUS.EXCEED_MAX_LIMIT) {
      store.commit('cart/setCartMaxLimitStatus', true);
      return;
    }
    if (!isFromCart) {
      setCartDataChangeStatus(true);
    }
    callBack(res);
  });
};

// add shopping cart
export const addCartListPromise = async (
  asin,
  merchantId,
  count = 1,
  selected = false,
  isFromCart
) => {
  const cartData = await tidyDataStructure(asin, merchantId, count, selected);

  return new Promise((resolve, reject) => {
    // Product out of stock or The product has been removed from the shelves
    if (
      cartData.productItem?.availability?.status ===
        AVAILABILITY_STATUS_ENUM.OUT_OF_STOCK ||
      !cartData.productItem.selectionEligible
    ) {
      showToast({
        message: t('common.PRODUCTS_HAVE_BEEN_SOLD_OUT_UNABLE_ADD_CART'),
        className: 'a-toast'
      });
      resolve({ success: false });
      return;
    }
    Bridge.callFnVal('addShoppingCartList', { item: cartData }).then(res => {
      switch (res.result) {
        case ADD_CART_AND_FAVORITES_STATUS.SUCCESSFUL:
          if (!isFromCart) {
            setCartDataChangeStatus(true);
          }
          resolve({ success: true });
          break;
        case ADD_CART_AND_FAVORITES_STATUS.FAILURE:
        case ADD_CART_AND_FAVORITES_STATUS.SINGLE_PRODUCT_EXCEED_MAX_LIMIT:
          resolve({ success: false });
          break;
        case ADD_CART_AND_FAVORITES_STATUS.EXCEED_MAX_LIMIT:
          store.commit('cart/setCartMaxLimitStatus', true);
          reject();
          break;
        default:
          break;
      }
    });
  });
};

// add shopping favorites
export const addFavoritesList = async (asin, merchantId, callBack) => {
  const cartData = await tidyDataStructure(asin, merchantId);
  Bridge.callFn('addFavoriteList', { item: cartData }, res => callBack(res));
};

const isFilterOutOfStockAndSoldOut = asinItem => {
  return (
    !asinItem.productItem.selectionEligible ||
    (asinItem.productItem?.availability &&
      asinItem.productItem.availability.status ===
        AVAILABILITY_STATUS_ENUM.OUT_OF_STOCK)
  );
};

// Store goods that need to be checkout
export const setCheckoutList = (list = [], callBack) => {
  const listAll = [];
  list.forEach(item => {
    listAll.push(tidyDataStructure(item.asin, item.merchantId, item.count));
  });
  Promise.all(listAll).then(lists => {
    // Determine out of stock and delisted products
    if (lists.some(isFilterOutOfStockAndSoldOut)) {
      const filterList = lists.filter(isFilterOutOfStockAndSoldOut);
      callBack({
        isOutOfStock: true,
        list: filterList
      });
      return;
    }
    lists.forEach((item, index) => {
      item.associateToken = list[index].associateToken;
      item.associateTokenTimeStamp = list[index].associateTokenTimeStamp;
    });
    store.commit('checkout/setCheckoutItemList', lists);
    Bridge.callFn(
      'saveDataToMemoryCache',
      {
        key: 'checkoutList',
        value: lists
      },
      () =>
        callBack({
          isOutOfStock: false
        })
    );
  });
};

// Get countdown
export const getDealCountDownTimeInMillis = endTime => {
  if (endTime) {
    return parseInt(endTime, 10) - new Date().getTime();
  }
  return null;
};
// Price separation Separate the decimal point from the front
export const priceSplice = price => {
  if (Number(price).toFixed !== 'NAN' && price) {
    const num = Number(price).toFixed(2);
    return [num.toString().split('.')[0], num.toString().split('.')[1]];
  }
  return ['0', '00'];
};
// Deep copies handle objects and arrays separately
export const deepClone = params => {
  // If array type data
  if (Array.isArray(params)) {
    const newList = [];
    for (let i = 0; i < params.length; i++) {
      newList[i] = deepClone(params[i]);
    }
    return newList;
  }
  // If it is object type data
  if (Object.prototype.toString.call(params) === '[object Object]') {
    const newObj = {};
    Object.keys(params).forEach(key => {
      newObj[key] = deepClone(params[key]);
    });
    return newObj;
  }
  return params;
};
// add service requestId
export const generateRandomString = (seed = 0) => {
  let text = seed;
  text = uuidv4();
  return text;
};

// if the YY is not as same as current year, then return MMDD; otherwise, reture YYYYMMDD
export const displayDate = day => {
  const yearStatus = new Date().getFullYear().toString();
  return dayjs(day).format('YYYY') === yearStatus
    ? dayjs(day).format(t('common.SHORT_DATE_FORMAT'))
    : dayjs(day).format(t('common.COMPLETE_DATE_FORMAT'));
};
export const getQueryString = (field, url) => {
  const href = url || window.location.href;
  const reg = new RegExp(`[?&]${field}=([^&]*)`, 'i');
  const string = reg.exec(href);
  return string ? decodeURIComponent(string[1].replaceAll('+', ' ')) : null;
};
// throttle
export const useThrottle = (fn, time) => {
  let timer = null;
  time = time || 1000;
  return function (...args) {
    if (timer) {
      return;
    }
    const that = this;
    timer = setTimeout(() => {
      timer = null;
    }, time);
    fn.apply(that, args);
  };
};
// copy text
export const onCopy = (text, toastMessage) => {
  const { copy, isSupported } = useClipboard();
  if (!isSupported) {
    showDialog({ message: 'Your browser does not support the Clipboard API' });
    return;
  }
  copy(text);
  showToast({
    icon: constants.SUCCESS_ICON_IMAGE_URL,
    overlay: false,
    overlayClass: 'a-overlay-white',
    message: toastMessage || t('common.copySucceed'),
    className: 'a-toast'
  });
};
const getInteractionPageType = interactionTypeId => {
  if (interactionTypeId && typeof interactionTypeId === 'string') {
    const list = interactionTypeId.split('_');
    if (list.length > 0) {
      return list[1];
    }
  }
  return '';
};
export function getUrlParams(url) {
  const params = {};
  const pattern = /[?&]+([^=&]+)=([^&]*)/gi;
  const matches = url.match(pattern);
  if (matches) {
    matches.forEach(match => {
      const [key, value] = match.split('=');
      params[key.substring(1)] = decodeURIComponent(value);
    });
  }
  return params;
}
const changeTabbarSelectItem = path => {
  const tabName = path.replaceAll('/', '');
  Bridge.callFn(
    'changeTabbarSelectItem',
    {
      selectIndex: TABBAR_PAGE_APP_CODE[tabName.toUpperCase()]
    },
    () => {}
  );
};
const isTabbarPage = url => {
  return Object.keys(TABBAR_PAGE_APP_CODE).some(tabBarPagePathItem => {
    return url.toUpperCase().indexOf(`/${tabBarPagePathItem}`) !== -1;
  });
};
export function parseAndOpenUrl(router, fullUrl) {
  if (fullUrl.includes(WEBAPP_PROD_SUBDOMAIN)) {
    if (isTabbarPage(fullUrl)) {
      if (isAppEnv()) {
        Bridge.callFn('getWebViewOriginalPagePath', {}, ({ path }) => {
          router?.push({
            path
          });
        });
        changeTabbarSelectItem(new URL(fullUrl).pathname);
      } else {
        router?.push({
          path: new URL(fullUrl).pathname
        });
      }
    } else {
      const params = getUrlParams(fullUrl);
      router.push({
        path: new URL(fullUrl).pathname,
        query: {
          ...params
        }
      });
    }
  } else {
    Bridge.callFn('openNativePage', {
      url: fullUrl,
      animated: false
    });
  }
}
// Jump interaction logic
export const jumpInteractionType = (modulesInfo, router) => {
  if (
    INTERACTION_TYPE.ROUTE_PAGE_TYPE.INTERNAL ===
    getInteractionPageType(modulesInfo.interactionTypeId1)
  ) {
    if (
      INTERACTION_TYPE.SECONDARY_INTERNAL_PAGE_TYPE.PRODUCT_LIST ===
      getInteractionPageType(modulesInfo.interactionTypeId2)
    ) {
      router?.push({
        name: modulesInfo.interactionTypePath,
        params: {
          keyword: modulesInfo.conditionMatch,
          type: 'search'
        }
      });
    } else if (
      INTERACTION_TYPE.SECONDARY_INTERNAL_PAGE_TYPE.CATEGORIZE ===
      getInteractionPageType(modulesInfo.interactionTypeId2)
    ) {
      const categoryIdList = [];
      // eslint-disable-next-line guard-for-in
      for (const categoryItem in modulesInfo.category) {
        if (categoryItem.indexOf('categoryId') !== -1) {
          categoryIdList.push(modulesInfo.category[categoryItem]);
        }
      }
      router?.push({
        name: modulesInfo.interactionTypePath,
        params: {
          keyword: JSON.stringify({
            value: categoryIdList[categoryIdList.length - 1],
            id: 'n'
          }),
          type: 'fromCategory'
        }
      });
    } else if (
      INTERACTION_TYPE.SECONDARY_INTERNAL_PAGE_TYPE.SECONDARY_ACTIVITIES ===
      getInteractionPageType(modulesInfo.interactionTypeId2)
    ) {
      router?.push({
        name: 'landingPage',
        query: {
          templateId: `${modulesInfo.selectedPage?.pageId}_${modulesInfo.selectedPage?.pageName}`
        }
      });
    } else if (
      TABBAR_PAGE_APP_CODE?.[modulesInfo.interactionTypePath.toUpperCase()] >= 0
    ) {
      if (isAppEnv()) {
        Bridge.callFn('getWebViewOriginalPagePath', {}, ({ path }) => {
          router?.push({
            path
          });
        });
        changeTabbarSelectItem(modulesInfo.interactionTypePath);
      } else {
        router?.push({
          name: modulesInfo.interactionTypePath
        });
      }
    } else {
      router?.push({
        name: modulesInfo.interactionTypePath
      });
    }
  } else if (
    INTERACTION_TYPE.ROUTE_PAGE_TYPE.CUSTOM ===
    getInteractionPageType(modulesInfo.interactionTypeId1)
  ) {
    parseAndOpenUrl(router, modulesInfo.pageUrl);
  } else if (
    INTERACTION_TYPE.ROUTE_PAGE_TYPE.PROMOTION_CUSTOMIZATION ===
    getInteractionPageType(modulesInfo.interactionTypeId1)
  ) {
    router?.push({
      name: modulesInfo.interactionTypePath
    });
  }
};
export function convertListToQueryParameter(listQueryParameter, parameterName) {
  return listQueryParameter
    .map(listElement => `${parameterName}=${encodeURIComponent(listElement)}`)
    .join('&');
}

export const formatTimeCountdown = (
  toDealEndTimeStamp,
  format,
  lastDayTimeDisplayFormat
) => {
  if (typeof toDealEndTimeStamp !== 'number' || typeof format !== 'string') {
    return toDealEndTimeStamp;
  }
  if (toDealEndTimeStamp <= 0) {
    return t('modules.detailPage.ENDED');
  }
  let result = format;
  if (
    lastDayTimeDisplayFormat &&
    typeof lastDayTimeDisplayFormat === 'string' &&
    toDealEndTimeStamp <= ONE_DAY_TIMESTAMP
  ) {
    result = lastDayTimeDisplayFormat;
  }
  TIMESTAMP_FORMAT_DENOMINATOR_ARR.forEach((denominator, index) => {
    if (result.indexOf(TIME_UNIT_ARR[index]) === -1) {
      return;
    }

    const quotient = Math.floor(toDealEndTimeStamp / denominator);
    result = result.replace(
      TIME_UNIT_ARR[index],
      ['m', 's'].includes(TIME_UNIT_ARR[index]) && quotient < 10
        ? `0${quotient}`
        : quotient
    );
    toDealEndTimeStamp -= quotient * denominator;
  });
  return result;
};
const SHOW_TOAST_DURATION = 2000; // 2000ms
export const showToastText = (
  text,
  success = false,
  duration = SHOW_TOAST_DURATION
) => {
  showToast({
    message: text,
    icon: success ? constants.SUCCESS_ICON_IMAGE_URL : '',
    overlay: false,
    overlayClass: 'a-overlay-white',
    className: 'a-toast',
    duration
  });
};
export const handleStartLogin = () => {
  Bridge.callFn('startLogin', null, () => {});
};
export const swiperDomTouchLogic = (lastSwiperDom, callBack) => {
  if (!lastSwiperDom) return;
  let startCilentX = 0;
  let endCilentX = 0;
  lastSwiperDom.addEventListener('touchstart', params => {
    startCilentX = params.touches[0].clientX;
  });
  // If the distance of the last element sliding is greater than 150, jump to the custom page
  lastSwiperDom.addEventListener('touchend', params => {
    endCilentX = params.changedTouches[0].clientX;
    if (endCilentX + 150 < startCilentX) callBack();
  });
};

export const isLoginAuthenticated = async (isShowStartLogin = true) => {
  const { result } = await Bridge.callFnVal('isAuthenticated', {});
  if (!result && isShowStartLogin) {
    handleStartLogin();
  }
  return result;
};

export const isInArray = (item, array) => {
  for (let i = 0; i < array.length; i++) {
    if (item.includes(array[i])) {
      return true;
    }
  }
  return false;
};

const getAppCurrentMode = () => Bridge.callFnVal('getAppCurrentMode', {});

const isAppBrowseMode = curMode => {
  return !curMode ? true : curMode === APP_BROWSE_MODE.BROWSE_MODE;
};

// Is there a PIPL alert present
export const isShowPIPLConfirmAlertView = async (isShowPIPL = true) => {
  const { appMode } = await getAppCurrentMode();
  const curBrowseMode = isAppBrowseMode(appMode);
  if (curBrowseMode && isShowPIPL) {
    Bridge.callFn('showPIPLConfirmAlertView', {}, () => {});
  }
  return curBrowseMode;
};
// Determine if it is on the same day
export const isInTheSameDay = (d1, d2) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate() &&
    date1.getHours() === date2.getHours() &&
    date1.getMinutes() === date2.getMinutes()
  );
};
export function handleCalcAccuracy(num1, num2) {
  const length1 = num1.toString().length - num1.toString().indexOf('.') - 1;
  const length2 = num2.toString().length - num2.toString().indexOf('.') - 1;
  const result = Math.max(length1, length2);
  return 10 ** result;
}
export function calcMultiplicative(num1, num2) {
  const accuracy = handleCalcAccuracy(num1, num2);
  return (num1 * accuracy * (num2 * accuracy)) / (accuracy * accuracy);
}
export function isObjectValueEqual(obj1, obj2) {
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
  const aProps = Reflect.ownKeys(obj1);
  const bProps = Reflect.ownKeys(obj2);
  if (aProps.length !== bProps.length) return false;
  for (let i = 0; i < aProps.length; i++) {
    const propName = aProps[i];
    const type = typeof obj1[propName];
    if (type === 'object') {
      if (!isObjectValueEqual(obj1[propName], obj2[propName])) return false;
    } else {
      if (obj1[propName] !== obj2[propName]) return false;
    }
  }
  return true;
}
// Return time division
export const displayTime = (day, formatString) => {
  const date = new Date(day.replaceAll('-', '/'));
  return dayjs(date).format(formatString);
};
export const getAccessToken = async () => {
  let token = '';
  if (isAppEnv()) {
    if (await isLoginAuthenticated(false)) {
      const { accessToken: tokenApp } = await Bridge.callFnVal(
        'getAccessToken',
        {}
      );
      token = tokenApp;
    }
  } else {
    token = sessionStorage.getItem('token') || '';
  }
  return { token };
};
export const getAppConfig = async type => {
  const cacheAppConfigData = store.state?.appConfigData?.value;
  const appConfig =
    cacheAppConfigData || (await Bridge.callFnVal('getAppConfig', {}));
  if (!cacheAppConfigData) {
    store.commit('setAppConfigData', appConfig);
  }
  return appConfig[type];
};

/**
 * add query to url
 * @param {*} value
 * @return type, data type of a var
 */
export const getType = value => {
  return Object.prototype.toString
    .call(value)
    .slice(8)
    .slice(0, -1)
    .toLowerCase();
};

/*
 *Remove white edges from the image and reset the image size
 *According to Amazon's rules, the image is followed by '_AC_SY${Number}'cut off white edges, maximum fill
 */
export const imageCutEdgeAndSetSize = (url, size = 164) => {
  if (getType(url) === 'string') {
    const urlArr = url.split('.');
    if (url.includes(MEDIA_CENTRAL_LINKS.MEDIA_CENTRAL_DOMAIN)) {
      urlArr.splice(urlArr.length - 1, 1, `_AC_SY${size}`);
    } else {
      urlArr.splice(urlArr.length - 1, 0, `_AC_SY${size}`);
    }
    return urlArr.join('.');
  }
  return url;
};
